
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Timer, Flame, Apple, Trophy, Clock } from "lucide-react";
import { Link } from "react-router-dom";

interface AdditionalStatsProps {
  favoriteRecipes: number;
  totalCookingTime: number;
  mostUsedIngredient: string;
  totalCalories: number;
  fastestRecipe?: {
    id: string;
    title: string;
    time: number;
  };
  longestRecipe?: {
    id: string;
    title: string;
    time: number;
  };
}

export const AdditionalStats = ({ 
  favoriteRecipes, 
  totalCookingTime, 
  mostUsedIngredient,
  totalCalories,
  fastestRecipe,
  longestRecipe
}: AdditionalStatsProps) => {
  // Calculate fun comparisons
  const marathons = Math.round((totalCalories / 2600) * 10) / 10; // Average calories burned in a marathon
  const cheeseburgers = Math.round(totalCalories / 303); // Average calories in a cheeseburger

  // Format time (converts minutes to hours and minutes)
  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Statistics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {/* Calories Stats */}
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
            <div className="flex items-start gap-3">
              <Flame className="w-5 h-5 text-orange-500 mt-1" />
              <div>
                <div className="font-medium text-gray-900">
                  Your recipes have powered {totalCalories.toLocaleString()} calories of air-fried goodness!
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  That's enough energy to run {marathons} marathons or equal to {cheeseburgers} cheeseburgers! 🍔
                </div>
              </div>
            </div>
          </div>

          {/* Recipe Time Champions */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Fastest Recipe */}
            {fastestRecipe && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-start gap-3">
                  <Timer className="w-5 h-5 text-green-500 mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">Speed Champion!</div>
                    <div className="text-sm text-gray-600 mt-1">
                      <Link 
                        to={`/recipe/${fastestRecipe.id}`}
                        className="text-primary hover:underline"
                      >
                        {fastestRecipe.title}
                      </Link>{' '}
                      takes just {formatTime(fastestRecipe.time)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Longest Recipe */}
            {longestRecipe && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-start gap-3">
                  <Trophy className="w-5 h-5 text-purple-500 mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">Marathon Winner!</div>
                    <div className="text-sm text-gray-600 mt-1">
                      <Link 
                        to={`/recipe/${longestRecipe.id}`}
                        className="text-primary hover:underline"
                      >
                        {longestRecipe.title}
                      </Link>{' '}
                      takes {formatTime(longestRecipe.time)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Original Stats */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-500 mt-1" />
                <div>
                  <div className="font-medium text-gray-900">Total Cooking Time</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {formatTime(totalCookingTime)}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
              <div className="flex items-start gap-3">
                <Apple className="w-5 h-5 text-yellow-500 mt-1" />
                <div>
                  <div className="font-medium text-gray-900">Most Used Ingredient</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {mostUsedIngredient}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
